// SlidePreview.tsx
import React, { FC } from 'react';
import styled from 'styled-components';
import { Asset, Entry } from 'contentful';

interface SlidePreviewProps {
  index: number;
  isActive: boolean;
  imageUrl?: string;
  altText?: string;
  caption?: string;
  theme: string;
  onClick: (index: number) => void;
}

interface SlidePreviewRailProps {
  slides: Entry[];
  activeIndex: number;
  theme: string;
  onPreviewClick: (idx: number) => void;
}

const Rail = styled.div`
  display: none;

  @media (min-width: 1024px) {
    display: grid;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 0;
    width: 90%;
    margin: 0 auto;
    gap: 4px;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    z-index: 1;
    pointer-events: all;
  }
`;

const Card = styled.button<{ $isActive: boolean; $theme: string }>`
  all: unset;
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px;
  box-sizing: border-box;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.8)};
  background: ${({ $theme }) => ($theme === 'dark' ? 'rgba(247, 247, 247, 0.10)' : 'rgba(25, 26, 31, 0.10)')};
  transition: opacity 0.2s ease-in-out, outline 0.2s ease-in-out;

  &:hover {
    opacity: 1;
  }

  ${({ $isActive }) =>
    $isActive &&
    `
    opacity: 1;
    `}
`;

const Thumb = styled.img`
  width: 52px;
  height: 52px;
  object-fit: cover;
  flex-shrink: 0;
`;

const Caption = styled.span<{ $theme: string }>`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.14px;
  line-height: 20px; /* 142.857% */
  color: ${({ $theme }) => ($theme === 'dark' ? '#fff' : '#191A1F')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SlidePreview: FC<SlidePreviewProps> = ({
  index,
  isActive,
  imageUrl,
  altText,
  caption,
  theme,
  onClick,
}) => (
  <Card
    $isActive={isActive}
    $theme={theme}
    onClick={() => onClick(index)}
    aria-current={isActive}
    aria-label={`Go to slide ${index + 1}`}
  >
    {imageUrl && <Thumb src={imageUrl} alt={altText || caption || `Slide ${index + 1}`} />}
    {caption && <Caption $theme={theme}>{caption}</Caption>}
  </Card>
);

const getImageUrl = (asset?: Asset): string | undefined => {
  if (!asset?.fields?.file?.url) return undefined;
  return `https:${asset.fields.file.url}?w=96&h=96&fit=thumb`;
};

export const SlidePreviewRail: FC<SlidePreviewRailProps> = ({
  slides,
  activeIndex,
  theme,
  onPreviewClick,
}) => (
  <Rail className="slide-preview-rail">
    {slides.map((slide, i) => {
      const imageAsset = (slide?.fields as any)?.slidePreview as Asset | undefined;
      const caption = (slide?.fields as any)?.slidePreviewTextContent as string | undefined;
      return (
        <SlidePreview
          key={slide.sys.id || i}
          index={i}
          isActive={i === activeIndex}
          imageUrl={getImageUrl(imageAsset)}
          altText={caption}
          caption={caption}
          theme={theme}
          onClick={onPreviewClick}
        />
      );
    })}
  </Rail>
);

export default SlidePreview;